import { useLayoutEffect, useRef, useState, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Button, Field, Sheet } from './ui';
import { useToast } from './feedback';

const NEW = '__novo__';

/**
 * Seletor de setor com "+ Novo setor…": o setor criado fica salvo para a plataforma toda.
 * `allowEmpty` mostra a opção "Escolha o setor" (fotos do modo rápido ainda sem setor).
 */
export function SectorSelect({ tid, sectors, value, onChange, allowEmpty, className = 'select sm', id }: {
  tid: string; sectors: string[]; value: string; onChange: (v: string) => void; allowEmpty?: boolean; className?: string; id?: string;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [nome, setNome] = useState('');
  const [busy, setBusy] = useState(false);
  const options = value && !sectors.includes(value) ? [...sectors, value] : sectors;

  const save = async () => {
    if (nome.trim().length < 2) return toast('Digite o nome do setor', 'err');
    setBusy(true);
    try {
      const s = await api.post<{ id: string; nome: string }>(`/api/t/${tid}/sectors`, { nome: nome.trim() });
      await qc.invalidateQueries({ queryKey: ['tenant', tid] });
      onChange(s.nome);
      setCreating(false);
      setNome('');
      toast(`Setor "${s.nome}" criado`);
    } catch (e) {
      toast(errMsg(e), 'err');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <select
        id={id}
        className={className}
        value={value}
        onChange={(e) => (e.target.value === NEW ? setCreating(true) : onChange(e.target.value))}
        style={!value ? { color: 'var(--nc-text)' } : undefined}
      >
        {(allowEmpty || !value) && <option value="">Escolha o setor</option>}
        {options.map((s) => <option key={s} value={s}>{s}</option>)}
        <option value={NEW}>+ Novo setor…</option>
      </select>
      <Sheet open={creating} onClose={() => setCreating(false)} title="Novo setor">
        <form onSubmit={(e) => { e.preventDefault(); void save(); }}>
          <Field label="Nome do setor" hint="Ex.: Lactário, Copa, Padaria, Área de lixo. Fica disponível nas próximas visitas.">
            <input className="input" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={60} autoFocus />
          </Field>
          <Button variant="dark" size="lg" block type="submit" loading={busy}>Criar setor</Button>
        </form>
      </Sheet>
    </>
  );
}

/** Campo de senha com botão "mostrar" — mais fácil para quem digita no celular. */
export function PasswordInput(props: InputHTMLAttributes<HTMLInputElement>) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input {...props} type={show ? 'text' : 'password'} className={`input ${props.className ?? ''}`} style={{ paddingRight: 84, ...props.style }} />
      <button
        type="button"
        className="link-btn"
        onClick={() => setShow((s) => !s)}
        style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12.5, fontWeight: 500 }}
        aria-label={show ? 'Esconder senha' : 'Mostrar senha'}
      >
        {show ? 'esconder' : 'mostrar'}
      </button>
    </div>
  );
}

/** Caixa de texto que cresce com o conteúdo (observações não têm limite de tamanho). */
export function AutoTextarea({ minRows = 2, style, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement> & { minRows?: number }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [props.value]);
  return <textarea ref={ref} rows={minRows} className="textarea" {...props} style={{ resize: 'none', overflow: 'hidden', ...style }} />;
}
